import React from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";

/**
 * Iter-432 — category sort dropdown (right-aligned on every category /
 * search grid). Iter 784 — options now come from the shared
 * `GET /classifieds/sort-options?category_slug=` endpoint so every pod
 * renders the identical set (vehicle year/kms sorts appear only for the
 * cars & bikes families). The backend default ("recent" = Featured first
 * + hourly rotation) maps to the empty value so URLs stay param-free by
 * default; "Latest listings" sends the NEW strict `sort=latest`.
 * `distance` never appears as a pickable option — but when nearest-first
 * is active we surface it as the selected state so the control stays
 * honest; picking anything else replaces it.
 */

const FALLBACK_OPTIONS = [
  { value: "", label: "Featured first" },
  { value: "latest", label: "Latest listings" },
  { value: "price_asc", label: "Lowest price" },
  { value: "price_desc", label: "Highest price" },
  { value: "title", label: "Title (A→Z)" },
];

export default function SortDropdown({ value = "", onChange, categorySlug = "" }) {
  const q = useQuery({
    queryKey: ["sort-options", categorySlug],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/sort-options", {
        params: categorySlug ? { category_slug: categorySlug } : undefined,
      });
      return data;
    },
    staleTime: 30 * 60_000,
    retry: 0,
  });

  const def = q.data?.default || "recent";
  const options = (q.data?.options?.length ? q.data.options : FALLBACK_OPTIONS).map((o) =>
    o.value === def ? { ...o, value: "" } : o,
  );
  const v = options.some((o) => o.value === value) || value === "distance" ? value : "";

  return (
    <label className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
      <span className="hidden sm:inline">Sort</span>
      <select
        data-testid="sort-dropdown"
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-full border border-soft bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 focus:outline-none focus:border-[color:var(--brand-blue)] cursor-pointer"
      >
        {v === "distance" && <option value="distance">Nearest first</option>}
        {options.map((o) => (
          <option key={o.value || "default"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
